import React, { useRef, useCallback } from 'react';
import { useTextSelection } from '../../hooks/useTextSelection';
import TextSelectionButton from './TextSelectionButton';
import TextSelectionDialog from './TextSelectionDialog';

interface SelectableContentProps {
  children: React.ReactNode;
  onTextSelected: (text: string, question: string) => void;
  isRTL?: boolean;
  className?: string;
  showSelection?: boolean;
}

const SelectableContent: React.FC<SelectableContentProps> = ({
  children,
  onTextSelected,
  isRTL = false,
  className = '',
  showSelection = true
}) => {
  const [showSelectionDialog, setShowSelectionDialog] = React.useState(false);
  const [savedSelection, setSavedSelection] = React.useState<{text: string, x: number, y: number} | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const { selection, clearSelection } = useTextSelection(contentRef);

  // Debug logging
  React.useEffect(() => {
    console.log('SelectableContent: selection changed:', selection);
    
    // Save selection when it becomes available
    if (selection && !showSelectionDialog) {
      console.log('SelectableContent: Saving selection');
      setSavedSelection({
        text: selection.text,
        x: selection.x,
        y: selection.y
      });
    }
    
    // Only reset dialog state if selection is cleared AND dialog is not open
    // (to prevent auto-focus from clearing the dialog)
    if (!selection && showSelectionDialog && !savedSelection) {
      console.log('SelectableContent: Selection cleared, resetting dialog state');
      setShowSelectionDialog(false);
    }
  }, [selection, showSelectionDialog, savedSelection]);

  React.useEffect(() => {
    console.log('SelectableContent: showSelectionDialog changed:', showSelectionDialog);
  }, [showSelectionDialog]);

  const handleButtonClick = useCallback(() => {
    console.log('SelectableContent: Button clicked, selection:', selection, 'savedSelection:', savedSelection);
    const activeSelection = selection || savedSelection;
    if (activeSelection) {
      console.log('SelectableContent: Setting showSelectionDialog to true');
      setShowSelectionDialog(true);
    }
  }, [selection, savedSelection]);

  const handleDialogSubmit = useCallback((question: string) => {
    const activeSelection = savedSelection || selection;
    if (activeSelection) {
      console.log('SelectableContent: Submitting question with text:', activeSelection.text);
      onTextSelected(activeSelection.text, question);
      clearSelection();
      setSavedSelection(null);
      setShowSelectionDialog(false);
    }
  }, [selection, savedSelection, onTextSelected, clearSelection]);

  const handleDialogClose = useCallback(() => {
    console.log('SelectableContent: Closing dialog');
    setShowSelectionDialog(false);
    setSavedSelection(null);
    clearSelection();
  }, [clearSelection]);

  // Use saved selection if available, otherwise use live selection
  const activeSelection = savedSelection || selection;

  return (
    <div className={`relative ${className}`}>
      <div 
        ref={contentRef}
        className="select-text"
        style={{
          userSelect: 'text',
          WebkitUserSelect: 'text',
          MozUserSelect: 'text',
          msUserSelect: 'text'
        }}
      >
        {children}
      </div>

      {/* Text Selection Button */}
      {showSelection && activeSelection && !showSelectionDialog && (
        <TextSelectionButton
          x={activeSelection.x}
          y={activeSelection.y}
          onClick={handleButtonClick}
          isRTL={isRTL}
        />
      )}

      {/* Text Selection Dialog */}
      {showSelection && showSelectionDialog && activeSelection && (
        <TextSelectionDialog
          x={activeSelection.x}
          y={activeSelection.y}
          selectedText={activeSelection.text}
          onSubmit={handleDialogSubmit}
          onClose={handleDialogClose}
          isRTL={isRTL}
        />
      )}
    </div>
  );
};

export default SelectableContent; 